import React, { useContext, useEffect, useMemo, useState } from 'react';
import { UserContext } from '../../../../context/UserContext';
import { useLocalStorage } from './hooks/useLocalStorage';
import { FORM_STATUS } from './types';
import {
  ALL_FILTER_VALUE,
  createClientControls,
  createFormControls,
  createNewFormControls,
  createTemplateControls,
  getTemplateCategoryKey,
} from './navigationUtils';
import {
  createFormPrerequisiteItems,
  createGuidanceModel,
  GUIDANCE_DISMISSALS_STORAGE_KEY,
  isHelpVisible,
} from './guidanceUtils';
import ClientManager from './components/ClientManager';
import TemplateBuilder from './components/TemplateBuilder';
import FormFiller from './components/FormFiller';
import Dashboard from './components/Dashboard';
import FormList from './components/FormList';
import NewFormForm from './components/NewFormForm';
import { useDynamicFormsData } from './hooks/useDynamicFormsData';
import {
  createDynamicFormsShareLink,
  downloadDynamicFormsResponsesCsv,
  getDynamicFormsShareLink,
  revokeDynamicFormsShareLink,
} from './api';

const SHARE_LINKS_STORAGE_KEY = 'ai-dynamic-forms-share-links';

const buildResponsesCsvFilename = (form) => {
  const baseTitle = (form?.title || `dynamic-form-${form?.id || 'responses'}`)
    .trim()
    .replace(/[^a-z0-9._-]+/gi, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

  return `${baseTitle || `dynamic-form-${form?.id || 'responses'}`}-responses.csv`;
};

const HELP_DISMISS_KEYS = {
  clients: 'clients-tab-help',
  templates: 'templates-tab-help',
  forms: 'forms-tab-help',
  newForm: 'new-form-help',
  clientForms: 'client-forms-help',
};

const App = ({ user }) => {
  const { user: contextUser } = useContext(UserContext);
  const resolvedUser = user ?? contextUser;
  const [currentView, setCurrentView] = useState('dashboard');
  const {
    templates,
    clients,
    forms,
    loading,
    error,
    reload,
    saveClient,
    deleteClient: deleteClientRecord,
    saveTemplate,
    deleteTemplate: deleteTemplateRecord,
    saveForm,
    deleteForm: deleteFormRecord,
  } = useDynamicFormsData(resolvedUser);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [selectedFormId, setSelectedFormId] = useState(null);
  const [navigationIntent, setNavigationIntent] = useState(null);
  const [clientCreateRequest, setClientCreateRequest] = useState(0);
  const [templateCreateRequest, setTemplateCreateRequest] = useState(0);
  const [formTitleFocusRequest, setFormTitleFocusRequest] = useState(0);
  const [formTitleFocusFormId, setFormTitleFocusFormId] = useState(null);
  const [shareDialogForm, setShareDialogForm] = useState(null);
  const [rememberedShareLinks, setRememberedShareLinks] = useLocalStorage(SHARE_LINKS_STORAGE_KEY, {});
  const [shareLinkState, setShareLinkState] = useState({
    loading: false,
    saving: false,
    error: null,
    link: null,
    copied: false,
  });
  const [shareOptions, setShareOptions] = useState({ allowMultiplePublicSubmissions: false });
  const [shareStatusByFormId, setShareStatusByFormId] = useState({});
  const [formSyncMessage, setFormSyncMessage] = useState(null);
  const [selectedFormReadOnlyMessage, setSelectedFormReadOnlyMessage] = useState(null);
  const [dismissedHelp, setDismissedHelp] = useLocalStorage(GUIDANCE_DISMISSALS_STORAGE_KEY, {});
  const [navigationControls, setNavigationControls] = useState(() => ({
    clients: createClientControls(),
    templates: createTemplateControls(),
    forms: createFormControls(),
    newForm: createNewFormControls(),
  }));

  const selectedTemplate = templates.find((template) => template.id === selectedTemplateId) || null;
  const selectedClient = clients.find((client) => client.id === selectedClientId) || null;
  const selectedForm = forms.find((form) => form.id === selectedFormId) || null;
  const guidanceModel = useMemo(() => createGuidanceModel(clients, templates, forms), [clients, templates, forms]);
  const prerequisiteItems = useMemo(
    () => createFormPrerequisiteItems(guidanceModel),
    [guidanceModel]
  );

  const clientsWithLinkedFormsCount = useMemo(
    () => clients.filter((client) => (guidanceModel.clientFormCounts[client.id] || 0) > 0).length,
    [clients, guidanceModel.clientFormCounts]
  );
  const templatesWithLinkedFormsCount = useMemo(
    () => templates.filter((template) => (guidanceModel.templateFormCounts[template.id] || 0) > 0).length,
    [guidanceModel.templateFormCounts, templates]
  );

  useEffect(() => {
    setNavigationControls((previousControls) => {
      let nextControls = previousControls;
      let changed = false;
      const templateIds = new Set(templates.map((template) => template.id));
      const clientIds = new Set(clients.map((client) => client.id));
      const templateCategories = new Set(
        templates.map((template) => getTemplateCategoryKey(template.category))
      );

      if (
        previousControls.templates.category !== ALL_FILTER_VALUE &&
        !templateCategories.has(previousControls.templates.category)
      ) {
        nextControls = {
          ...nextControls,
          templates: {
            ...nextControls.templates,
            category: ALL_FILTER_VALUE,
          },
        };
        changed = true;
      }

      if (
        previousControls.forms.templateId !== ALL_FILTER_VALUE &&
        !templateIds.has(previousControls.forms.templateId)
      ) {
        nextControls = {
          ...nextControls,
          forms: {
            ...nextControls.forms,
            templateId: ALL_FILTER_VALUE,
          },
        };
        changed = true;
      }

      if (
        previousControls.forms.clientId !== ALL_FILTER_VALUE &&
        !clientIds.has(previousControls.forms.clientId)
      ) {
        nextControls = {
          ...nextControls,
          forms: {
            ...nextControls.forms,
            clientId: ALL_FILTER_VALUE,
          },
        };
        changed = true;
      }

      return changed ? nextControls : previousControls;
    });
  }, [clients, templates]);

  const updateControls = (key, updates) => {
    setNavigationControls((previousControls) => {
      const currentControls = previousControls[key];
      const nextControls = typeof updates === 'function'
        ? updates(currentControls)
        : { ...currentControls, ...updates };

      return {
        ...previousControls,
        [key]: nextControls,
      };
    });
  };

  const dismissHelp = (dismissKey) => {
    setDismissedHelp((previousState = {}) => ({
      ...previousState,
      [dismissKey]: true,
    }));
  };

  const showHelp = (dismissKey) => {
    setDismissedHelp((previousState = {}) => {
      const nextState = { ...previousState };
      delete nextState[dismissKey];
      return nextState;
    });
  };

  const resolveEntityById = (items, id) => items.find((item) => item.id === id) || null;

  const getIntentParams = (intent, overrides = {}) => ({
    client:
      overrides.client ||
      resolveEntityById(clients, overrides.clientId ?? intent?.clientId ?? null) ||
      null,
    template:
      overrides.template ||
      resolveEntityById(templates, overrides.templateId ?? intent?.templateId ?? null) ||
      null,
  });

  const navigateTo = (view, params = {}, options = {}) => {
    setCurrentView(view);
    setSelectedTemplateId(params.template?.id ?? null);
    setSelectedClientId(params.client?.id ?? null);
    setSelectedFormId(params.form?.id ?? null);
    setSelectedFormReadOnlyMessage(params.readOnlyMessage || null);

    if (!options.preserveIntent) {
      setNavigationIntent(null);
    }
  };

  const navigateWithGuidance = (view, params = {}) => {
    navigateTo(view, params);
  };

  const buildPublicShareUrl = (token) => {
    if (!token) {
      return '';
    }

    const { origin, pathname } = window.location;
    return `${origin}${pathname}#/ai/dynamicforms/public/${encodeURIComponent(token)}`;
  };

  const rememberShareLink = (formId, link) => {
    if (!formId || !link?.active || !link?.token) {
      return;
    }

    setRememberedShareLinks((previousLinks) => ({
      ...previousLinks,
      [formId]: link.token,
    }));
  };

  const forgetRememberedShareLink = (formId) => {
    if (!formId) {
      return;
    }

    setRememberedShareLinks((previousLinks) => {
      if (!previousLinks?.[formId]) {
        return previousLinks;
      }

      const nextLinks = { ...previousLinks };
      delete nextLinks[formId];
      return nextLinks;
    });
  };

  const withRememberedShareLink = (formId, link) => {
    if (!link?.active || link.token || !rememberedShareLinks?.[formId]) {
      return link;
    }

    return {
      ...link,
      token: rememberedShareLinks[formId],
      remembered: true,
    };
  };

  const refreshShareLinkStatus = async (form, options = {}) => {
    if (!form?.id) {
      return null;
    }

    setShareStatusByFormId((previousState) => ({
      ...previousState,
      [form.id]: {
        ...(previousState[form.id] || {}),
        loading: true,
        error: null,
      },
    }));

    try {
      const link = withRememberedShareLink(form.id, await getDynamicFormsShareLink(resolvedUser, form.id));
      const status = {
        loading: false,
        active: Boolean(link?.active),
        link,
        error: null,
        checkedAt: Date.now(),
      };
      setShareStatusByFormId((previousState) => ({
        ...previousState,
        [form.id]: status,
      }));

      if (options.showMessage) {
        setFormSyncMessage(
          status.active
            ? 'This form is shared. It will become editable once the shared link is deleted.'
            : 'Form synced. No active shared link was found.'
        );
      }

      return status;
    } catch (shareError) {
      console.error('Failed to load dynamic form share-link status:', shareError);
      const status = {
        loading: false,
        active: false,
        link: null,
        error: 'Unable to check the share-link status.',
        checkedAt: Date.now(),
      };
      setShareStatusByFormId((previousState) => ({
        ...previousState,
        [form.id]: status,
      }));

      if (options.showMessage) {
        setFormSyncMessage('Unable to sync the form right now. Please try again.');
      }

      return status;
    }
  };

  const syncForm = async (form, options = {}) => {
    if (!form?.id) {
      return null;
    }

    const latestForms = await reload();
    const latestForm = latestForms?.forms?.find((entry) => entry.id === form.id) || form;
    await refreshShareLinkStatus(latestForm, { showMessage: options.showMessage });
    return latestForm;
  };

  const openFormForEdit = async (form) => {
    const latestForm = await syncForm(form);
    const shareStatus = await refreshShareLinkStatus(latestForm);

    if (shareStatus?.active) {
      const readOnlyMessage = 'This form is shared. It will become editable once the shared link is deleted.';
      setFormSyncMessage(null);
      setFormTitleFocusFormId(null);
      navigateTo('fill-form', { form: latestForm, readOnlyMessage });
      return;
    }

    setFormSyncMessage(null);
    setFormTitleFocusFormId(latestForm.id);
    setFormTitleFocusRequest((previousValue) => previousValue + 1);
    navigateTo('fill-form', { form: latestForm });
  };

  const openShareDialog = async (form) => {
    setShareDialogForm(form);
    setShareOptions({ allowMultiplePublicSubmissions: Boolean(form?.allowMultiplePublicSubmissions) });
    setShareLinkState({ loading: true, saving: false, error: null, link: null, copied: false });

    try {
      const status = await refreshShareLinkStatus(form);
      setShareLinkState({ loading: false, saving: false, error: null, link: status?.link || null, copied: false });
    } catch (shareError) {
      console.error('Failed to load dynamic form share link:', shareError);
      setShareLinkState({
        loading: false,
        saving: false,
        error: 'Unable to load the share-link status.',
        link: null,
        copied: false,
      });
    }
  };

  const closeShareDialog = () => {
    setShareDialogForm(null);
    setShareOptions({ allowMultiplePublicSubmissions: false });
    setShareLinkState({ loading: false, saving: false, error: null, link: null, copied: false });
  };

  const createShareLink = async () => {
    if (!shareDialogForm) {
      return;
    }

    setShareLinkState((previousState) => ({ ...previousState, saving: true, error: null, copied: false }));

    try {
      let currentShareDialogForm = shareDialogForm;
      if (Boolean(shareDialogForm.allowMultiplePublicSubmissions) !== shareOptions.allowMultiplePublicSubmissions) {
        currentShareDialogForm = await updateForm(shareDialogForm.id, {
          allowMultiplePublicSubmissions: shareOptions.allowMultiplePublicSubmissions,
        });
        setShareDialogForm(currentShareDialogForm);
      }

      const link = await createDynamicFormsShareLink(resolvedUser, currentShareDialogForm.id);
      rememberShareLink(currentShareDialogForm.id, link);
      setShareStatusByFormId((previousState) => ({
        ...previousState,
        [currentShareDialogForm.id]: { loading: false, active: Boolean(link?.active), link, error: null, checkedAt: Date.now() },
      }));
      setShareLinkState({ loading: false, saving: false, error: null, link, copied: false });
    } catch (shareError) {
      console.error('Failed to create dynamic form share link:', shareError);
      setShareLinkState((previousState) => ({
        ...previousState,
        saving: false,
        error: 'Unable to create a share link.',
      }));
    }
  };

  const revokeShareLink = async () => {
    if (!shareDialogForm || !window.confirm('Revoke this public share link? Anyone with the current link will lose access.')) {
      return;
    }

    setShareLinkState((previousState) => ({ ...previousState, saving: true, error: null, copied: false }));

    try {
      const link = await revokeDynamicFormsShareLink(resolvedUser, shareDialogForm.id);
      forgetRememberedShareLink(shareDialogForm.id);
      setShareStatusByFormId((previousState) => ({
        ...previousState,
        [shareDialogForm.id]: { loading: false, active: false, link, error: null, checkedAt: Date.now() },
      }));
      setShareLinkState({ loading: false, saving: false, error: null, link, copied: false });
    } catch (shareError) {
      console.error('Failed to revoke dynamic form share link:', shareError);
      setShareLinkState((previousState) => ({
        ...previousState,
        saving: false,
        error: 'Unable to revoke the share link.',
      }));
    }
  };

  const copyTextToClipboard = async (text) => {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return;
    }

    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.setAttribute('readonly', '');
    textArea.style.position = 'fixed';
    textArea.style.top = '-9999px';
    textArea.style.left = '-9999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    const copied = document.execCommand('copy');
    document.body.removeChild(textArea);

    if (!copied) {
      throw new Error('Copy command was not successful.');
    }
  };


  const downloadResponsesCsv = async (form) => {
    if (!form?.id) {
      return;
    }

    try {
      const timeZoneId = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const csv = await downloadDynamicFormsResponsesCsv(resolvedUser, form.id, timeZoneId);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = buildResponsesCsvFilename(form);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (downloadError) {
      console.error('Failed to download dynamic form responses CSV:', downloadError);
      setShareLinkState((previousState) => ({
        ...previousState,
        error: 'Unable to download responses CSV.',
      }));
    }
  };

  const copyShareLink = async () => {
    const url = buildPublicShareUrl(shareLinkState.link?.token);

    if (!url) {
      return;
    }

    try {
      await copyTextToClipboard(url);
      setShareLinkState((previousState) => ({ ...previousState, copied: true, error: null }));
    } catch (_clipboardError) {
      setShareLinkState((previousState) => ({
        ...previousState,
        copied: false,
        error: 'Unable to copy the link automatically. Select the link field and copy it manually.',
      }));
    }
  };

  const startClientCreationFlow = (targetView, params = {}) => {
    setNavigationIntent({
      targetView,
      clientId: params.client?.id ?? selectedClientId ?? null,
      templateId: params.template?.id ?? selectedTemplateId ?? null,
      missingRequirements: guidanceModel.missingRequirements,
    });
    setClientCreateRequest((previousValue) => previousValue + 1);
    navigateTo('clients', {}, { preserveIntent: true });
  };

  const startTemplateCreationFlow = (targetView, params = {}) => {
    setNavigationIntent({
      targetView,
      clientId: params.client?.id ?? selectedClientId ?? null,
      templateId: params.template?.id ?? selectedTemplateId ?? null,
      missingRequirements: guidanceModel.missingRequirements,
    });
    setTemplateCreateRequest((previousValue) => previousValue + 1);
    navigateTo('templates', {}, { preserveIntent: true });
  };

  const toPersistedId = (value) => (Number.isInteger(value) ? value : null);

  const buildClientPayload = (client = {}) => ({
    ...(toPersistedId(client.id) ? { id: client.id } : {}),
    name: client.name,
    location: client.location || '',
    contactName: client.contactName || '',
    contactEmail: client.contactEmail || null,
    contactPhone: client.contactPhone || '',
    notes: client.notes || '',
  });

  const buildTemplatePayload = (template = {}) => ({
    ...(toPersistedId(template.id) ? { id: template.id } : {}),
    name: template.name,
    description: template.description || '',
    category: template.category || 'interview',
    sections: template.sections || [],
    ...(template.status ? { status: template.status } : {}),
  });

  const buildFormPayload = (form = {}) => ({
    ...(toPersistedId(form.id) ? { id: form.id } : {}),
    clientId: form.clientId,
    templateId: form.templateId,
    title: form.title || '',
    status: form.status || FORM_STATUS.DRAFT,
    data: form.data || {},
    allowMultiplePublicSubmissions: Boolean(form.allowMultiplePublicSubmissions),
  });

  const buildDefaultFormData = (template) => {
    const data = {};

    (template?.sections || []).forEach((section) => {
      (section.fields || []).forEach((field) => {
        if (field?.name && field.defaultValue !== undefined && field.defaultValue !== '') {
          data[field.name] = field.defaultValue;
        }
      });
    });

    return data;
  };

  const addClient = async (clientData) => {
    const newClient = await saveClient(buildClientPayload(clientData));

    if (navigationIntent) {
      const targetView = navigationIntent.targetView || 'new-form';
      navigateTo(targetView, getIntentParams(navigationIntent, { client: newClient }));
    }

    return newClient;
  };

  const updateClient = async (clientId, updates) => {
    const existingClient = clients.find((client) => client.id === clientId);
    if (!existingClient) {
      return null;
    }

    return saveClient(buildClientPayload({
      ...existingClient,
      ...updates,
      id: clientId,
    }));
  };

  const deleteClient = async (clientId) => {
    const relatedForms = forms.filter((form) => form.clientId === clientId);
    const relatedFormCount = relatedForms.length;
    const message = relatedFormCount > 0
      ? `Are you sure you want to delete this client? ${relatedFormCount} associated form${relatedFormCount === 1 ? '' : 's'} will also be deleted.`
      : 'Are you sure you want to delete this client?';

    if (!window.confirm(message)) {
      return false;
    }

    await deleteClientRecord(clientId);

    if (selectedClientId === clientId) {
      setSelectedClientId(null);
      if (currentView === 'client-forms') {
        setCurrentView('clients');
      }
    }

    if (relatedForms.some((form) => form.id === selectedFormId)) {
      setSelectedFormId(null);
      if (currentView === 'fill-form') {
        setCurrentView('dashboard');
      }
    }

    return true;
  };

  const addTemplate = async (templateData) => {
    const newTemplate = await saveTemplate(buildTemplatePayload(templateData));

    if (navigationIntent) {
      const targetView = navigationIntent.targetView || 'new-form';
      navigateTo(targetView, getIntentParams(navigationIntent, { template: newTemplate }));
    }

    return newTemplate;
  };

  const updateTemplate = async (templateId, updates) => {
    const existingTemplate = templates.find((template) => template.id === templateId);
    if (!existingTemplate) {
      return null;
    }

    return saveTemplate(buildTemplatePayload({
      ...existingTemplate,
      ...updates,
      id: templateId,
    }));
  };

  const deleteTemplate = async (templateId) => {
    const relatedForms = forms.filter((form) => form.templateId === templateId);
    const relatedFormCount = relatedForms.length;
    const message = relatedFormCount > 0
      ? `Are you sure you want to delete this template? ${relatedFormCount} associated form${relatedFormCount === 1 ? '' : 's'} will also be deleted.`
      : 'Are you sure you want to delete this template?';

    if (!window.confirm(message)) {
      return false;
    }

    await deleteTemplateRecord(templateId);

    if (selectedTemplateId === templateId) {
      setSelectedTemplateId(null);
      if (currentView === 'edit-template') {
        setCurrentView('templates');
      }
    }

    if (relatedForms.some((form) => form.id === selectedFormId)) {
      setSelectedFormId(null);
      if (currentView === 'fill-form') {
        setCurrentView('dashboard');
      }
    }

    return true;
  };

  const createForm = async (templateId, clientId) => {
    const template = templates.find((entry) => entry.id === templateId);
    return saveForm(buildFormPayload({
      templateId,
      clientId,
      title: '',
      status: FORM_STATUS.DRAFT,
      data: buildDefaultFormData(template),
    }));
  };

  const updateForm = async (formId, updates) => {
    const existingForm = forms.find((form) => form.id === formId);
    if (!existingForm) {
      return null;
    }

    return saveForm(buildFormPayload({
      ...existingForm,
      ...updates,
      id: formId,
    }));
  };

  const deleteForm = async (formId) => {
    if (!window.confirm('Are you sure you want to delete this form?')) {
      return false;
    }

    await deleteFormRecord(formId);

    if (selectedFormId === formId) {
      setSelectedFormId(null);
      if (currentView === 'fill-form') {
        setCurrentView('dashboard');
      }
    }

    return true;
  };

  const buildVisibleHelpConfig = (config) => {
    if (!config) {
      return null;
    }

    const collapsed = !isHelpVisible(dismissedHelp, config.dismissKey, config.forceVisible);

    return {
      ...config,
      collapsed,
      onDismiss: config.forceVisible || collapsed ? null : () => dismissHelp(config.dismissKey),
      onExpand: collapsed ? () => showHelp(config.dismissKey) : null,
    };
  };

  const formsHelpActions = [];

  if (!guidanceModel.hasClients) {
    formsHelpActions.push({
      id: 'create-client',
      label: 'Create missing client',
      tone: 'primary',
      onClick: () => startClientCreationFlow('forms'),
    });
  }

  if (!guidanceModel.hasTemplates) {
    formsHelpActions.push({
      id: 'create-template',
      label: 'Create missing template',
      tone: formsHelpActions.length === 0 ? 'primary' : 'secondary',
      onClick: () => startTemplateCreationFlow('forms'),
    });
  }

  if (guidanceModel.canCreateForms) {
    formsHelpActions.push({
      id: 'create-form',
      label: 'Create new form',
      tone: 'primary',
      onClick: () => navigateWithGuidance('new-form'),
    });
  }

  const newFormHelpActions = [];

  if (!guidanceModel.hasClients) {
    newFormHelpActions.push({
      id: 'create-client',
      label: 'Create missing client',
      tone: 'primary',
      onClick: () => startClientCreationFlow('new-form', {
        client: selectedClient,
        template: selectedTemplate,
      }),
    });
  }

  if (!guidanceModel.hasTemplates) {
    newFormHelpActions.push({
      id: 'create-template',
      label: 'Create missing template',
      tone: newFormHelpActions.length === 0 ? 'primary' : 'secondary',
      onClick: () => startTemplateCreationFlow('new-form', {
        client: selectedClient,
        template: selectedTemplate,
      }),
    });
  }

  if (guidanceModel.canCreateForms) {
    newFormHelpActions.push({
      id: 'browse-forms',
      label: 'Browse existing forms',
      tone: 'secondary',
      onClick: () => navigateWithGuidance('forms'),
    });
  }

  const clientFormsActions = [];

  if (selectedClient) {
    if (guidanceModel.hasTemplates) {
      clientFormsActions.push({
        id: 'create-client-form',
        label: `Create form for ${selectedClient.name}`,
        tone: 'primary',
        onClick: () => navigateWithGuidance('new-form', { client: selectedClient }),
      });
    } else {
      clientFormsActions.push({
        id: 'create-template',
        label: 'Create missing template',
        tone: 'primary',
        onClick: () => startTemplateCreationFlow('new-form', { client: selectedClient }),
      });
    }

    clientFormsActions.push({
      id: 'back-to-clients',
      label: 'Back to clients',
      tone: 'secondary',
      onClick: () => navigateTo('clients'),
    });
  }

  const viewHelpConfig = {
    clients: {
      dismissKey: HELP_DISMISS_KEYS.clients,
      variant: clientsWithLinkedFormsCount > 0 ? 'warning' : 'info',
      title: 'Clients anchor every form record',
      summary: 'Create or update the client first, then reuse that record across many forms.',
      purpose: 'Manage the organizations or individuals that form instances belong to.',
      prerequisites: prerequisiteItems,
      steps: [
        'Add the client details that will help you recognize the record later.',
        'Open View Forms to see work already linked to that client.',
        'Use Create New Form after both a client and template are available.',
      ],
      note: clientsWithLinkedFormsCount > 0
        ? `${clientsWithLinkedFormsCount} client${clientsWithLinkedFormsCount === 1 ? '' : 's'} already linked to existing forms. Deleting a client also deletes those forms.`
        : 'Once a client exists, it becomes available anywhere the app asks you to pick a client.',
      actions: [
        {
          id: 'add-client',
          label: 'Add new client',
          tone: 'primary',
          onClick: () => setClientCreateRequest((previousValue) => previousValue + 1),
        },
        {
          id: 'new-form',
          label: 'Create new form',
          tone: 'secondary',
          onClick: () => navigateWithGuidance('new-form'),
          disabled: !guidanceModel.canCreateForms,
        },
      ],
    },
    templates: {
      dismissKey: HELP_DISMISS_KEYS.templates,
      variant: templatesWithLinkedFormsCount > 0 ? 'warning' : 'info',
      title: 'Templates define reusable form structure',
      summary: 'A template becomes the blueprint for every form created from it.',
      purpose: 'Create the sections and fields users will fill in later for each client.',
      prerequisites: prerequisiteItems,
      steps: [
        'Create the template shell with a name, description, and category.',
        'Add sections to organize the interview or assessment flow.',
        'Add fields inside each section, then save before creating form instances.',
      ],
      note: templatesWithLinkedFormsCount > 0
        ? `${templatesWithLinkedFormsCount} template${templatesWithLinkedFormsCount === 1 ? '' : 's'} already linked to existing forms. Deleting a template also deletes those forms.`
        : 'Once a template exists, it becomes available in the Create New Form flow.',
      actions: [
        {
          id: 'add-template',
          label: 'Create new template',
          tone: 'primary',
          onClick: () => setTemplateCreateRequest((previousValue) => previousValue + 1),
        },
        {
          id: 'new-form',
          label: 'Create new form',
          tone: 'secondary',
          onClick: () => navigateWithGuidance('new-form'),
          disabled: !guidanceModel.canCreateForms,
        },
      ],
    },
    forms: {
      dismissKey: HELP_DISMISS_KEYS.forms,
      forceVisible: !guidanceModel.canCreateForms,
      variant: guidanceModel.canCreateForms ? 'info' : 'warning',
      title: 'Forms combine one template with one client',
      summary: 'Use this tab to review existing form instances or start a new one when prerequisites are ready.',
      purpose: 'Browse saved work, filter it by status, and jump into the exact form you need to continue editing.',
      prerequisites: prerequisiteItems,
      steps: guidanceModel.canCreateForms
        ? [
            'Use filters to narrow the form list by status, template, or client.',
            'Open Edit on any row to continue working in that form.',
            'Use Create New Form when you need another instance for a different client or template pairing.',
          ]
        : [
            'You can still review existing forms here if any already exist.',
            'Create the missing prerequisites first before starting a new form.',
            'Return here to browse and filter active work once setup is complete.',
          ],
      note: guidanceModel.canCreateForms
        ? (forms.length === 0
          ? 'No forms exist yet. Use Create New Form to start the first one.'
          : 'This tab is best for finding and resuming existing work once the form library grows.')
        : 'New form creation stays blocked until both a client and a template are available.',
      actions: formsHelpActions,
    },
    newForm: {
      dismissKey: HELP_DISMISS_KEYS.newForm,
      forceVisible: !guidanceModel.canCreateForms,
      variant: guidanceModel.canCreateForms ? 'info' : 'warning',
      title: 'Create New Form is the final assembly step',
      summary: 'Pick one template and one client here to create a form instance. If setup is incomplete, fix the missing piece and come right back.',
      purpose: 'Assemble a new form from the reusable template structure and the specific client record it belongs to.',
      prerequisites: prerequisiteItems,
      steps: [
        'Choose the template that matches the interview or assessment you need.',
        'Choose the client record that should own this form.',
        'Create the form and continue directly into data entry.',
      ],
      note: guidanceModel.canCreateForms
        ? 'Selections made here carry straight into the editable form.'
        : 'When you create the missing client or template from here, the app returns you to this setup flow automatically.',
      actions: newFormHelpActions,
    },
    clientForms: selectedClient && {
      dismissKey: HELP_DISMISS_KEYS.clientForms,
      forceVisible: !guidanceModel.hasTemplates,
      variant: guidanceModel.hasTemplates ? 'info' : 'warning',
      title: `Forms for ${selectedClient.name}`,
      summary: 'This view is already filtered to one client, so you can review history or create another form without reselecting the client.',
      purpose: 'Keep work centered on one client record and jump between its related forms quickly.',
      prerequisites: prerequisiteItems,
      steps: [
        'Use the list below to reopen that client�s existing forms.',
        'Use Create New Form to keep this client preselected in the next step.',
        'If a template is missing, create it first and then resume the form flow.',
      ],
      note: guidanceModel.hasTemplates
        ? `${guidanceModel.clientFormCounts[selectedClient.id] || 0} linked form${guidanceModel.clientFormCounts[selectedClient.id] === 1 ? '' : 's'} currently belong to this client.`
        : 'This client is ready, but a template is still required before another form can be created.',
      actions: clientFormsActions,
    },
  };

  const visibleHelpConfig = {
    clients: buildVisibleHelpConfig(viewHelpConfig.clients),
    templates: buildVisibleHelpConfig(viewHelpConfig.templates),
    forms: buildVisibleHelpConfig(viewHelpConfig.forms),
    newForm: buildVisibleHelpConfig(viewHelpConfig.newForm),
    clientForms: buildVisibleHelpConfig(viewHelpConfig.clientForms),
  };

  const errorMessage = error?.message || error?.details || 'Failed to load dynamic forms.';

  if (!resolvedUser) {
    return (
      <div className="app-container dynamicforms-page">
        <main className="main-content">
          <div className="card">
            <h2 className="card-title">Dynamic Forms Unavailable</h2>
            <p>Sign in before using Dynamic Forms.</p>
          </div>
        </main>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="app-container dynamicforms-page">
        <main className="main-content">
          <div className="card">
            <h2 className="card-title">Loading Dynamic Forms</h2>
            <p>Fetching clients, templates, and forms from the server.</p>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="app-container dynamicforms-page">
        <main className="main-content">
          <div className="card">
            <h2 className="card-title">Unable to Load Dynamic Forms</h2>
            <p>{errorMessage}</p>
            <button className="btn btn-primary" onClick={() => reload().catch(() => {})}>
              Retry
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="app-container dynamicforms-page">
      <nav className="navbar">
        <div className="navbar-content">
          <div className="navbar-logo">FQHC Dynamic Forms</div>
          <div className="navbar-nav">
            <button
              className={`nav-item ${currentView === 'dashboard' ? 'active' : ''}`}
              onClick={() => navigateTo('dashboard')}
            >
              Dashboard
            </button>
            <button
              className={`nav-item ${currentView === 'clients' ? 'active' : ''}`}
              onClick={() => navigateTo('clients')}
            >
              Clients
            </button>
            <button
              className={`nav-item ${currentView === 'templates' ? 'active' : ''}`}
              onClick={() => navigateTo('templates')}
            >
              Templates
            </button>
            <button
              className={`nav-item ${currentView === 'forms' ? 'active' : ''}`}
              onClick={() => navigateWithGuidance('forms')}
            >
              Forms
            </button>
          </div>
        </div>
      </nav>

      <main className="main-content">
        {currentView === 'dashboard' && (
          <Dashboard
            templates={templates}
            clients={clients}
            forms={forms}
            onNavigate={navigateWithGuidance}
            onEditForm={openFormForEdit}
            onSyncForm={(form) => syncForm(form, { showMessage: true })}
            shareStatusByFormId={shareStatusByFormId}
          />
        )}

        {currentView === 'clients' && (
          <ClientManager
            clients={clients}
            onAdd={addClient}
            onUpdate={updateClient}
            onDelete={deleteClient}
            onSelect={(client) => navigateTo('client-forms', { client })}
            controls={navigationControls.clients}
            onControlsChange={(updates) => updateControls('clients', updates)}
            helpConfig={visibleHelpConfig.clients}
            openFormRequest={clientCreateRequest}
            formCounts={guidanceModel.clientFormCounts}
          />
        )}

        {currentView === 'templates' && (
          <TemplateBuilder
            templates={templates}
            onAdd={addTemplate}
            onUpdate={updateTemplate}
            onDelete={deleteTemplate}
            onSelect={(template) => navigateTo('edit-template', { template })}
            controls={navigationControls.templates}
            onControlsChange={(updates) => updateControls('templates', updates)}
            helpConfig={visibleHelpConfig.templates}
            openFormRequest={templateCreateRequest}
            formCounts={guidanceModel.templateFormCounts}
          />
        )}

        {currentView === 'edit-template' && selectedTemplate && (
          <TemplateBuilder
            templates={templates}
            template={selectedTemplate}
            onUpdate={updateTemplate}
            onClose={() => navigateTo('templates')}
            isEditing={true}
            controls={navigationControls.templates}
            onControlsChange={(updates) => updateControls('templates', updates)}
            openFormRequest={templateCreateRequest}
            formCounts={guidanceModel.templateFormCounts}
          />
        )}

        {currentView === 'forms' && (
          <div className="card">
            <h2 className="card-title">All Forms</h2>
            {formSyncMessage && <div className="alert alert-info">{formSyncMessage}</div>}
            <div style={{ marginBottom: '20px' }}>
              <button className="btn btn-primary" onClick={() => navigateWithGuidance('new-form')}>
                + Create New Form
              </button>
            </div>
            <FormList
              forms={forms}
              templates={templates}
              clients={clients}
              onSelect={openFormForEdit}
              onSync={(form) => syncForm(form, { showMessage: true })}
              onDelete={deleteForm}
              onShare={openShareDialog}
              shareStatusByFormId={shareStatusByFormId}
              controls={navigationControls.forms}
              onControlsChange={(updates) => updateControls('forms', updates)}
              showClientFilter={true}
              helpConfig={visibleHelpConfig.forms}
            />
          </div>
        )}

        {currentView === 'client-forms' && selectedClient && (
          <div className="card">
            <h2 className="card-title">Forms for {selectedClient.name}</h2>
            {formSyncMessage && <div className="alert alert-info">{formSyncMessage}</div>}
            <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button
                className="btn btn-primary"
                onClick={() => navigateWithGuidance('new-form', { client: selectedClient })}
              >
                + Create New Form
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => navigateTo('clients')}
              >
                Back to Clients
              </button>
            </div>
            <FormList
              forms={forms.filter((form) => form.clientId === selectedClient.id)}
              templates={templates}
              clients={clients}
              onSelect={openFormForEdit}
              onSync={(form) => syncForm(form, { showMessage: true })}
              onDelete={deleteForm}
              onShare={openShareDialog}
              shareStatusByFormId={shareStatusByFormId}
              controls={navigationControls.forms}
              onControlsChange={(updates) => updateControls('forms', updates)}
              showClientFilter={false}
              helpConfig={visibleHelpConfig.clientForms}
            />
          </div>
        )}

        {currentView === 'new-form' && (
          <div className="card">
            <h2 className="card-title">Create New Form</h2>
            <NewFormForm
              templates={templates}
              clients={clients}
              preselectedClient={selectedClient}
              preselectedTemplate={selectedTemplate}
              onCreate={async (templateId, clientId) => {
                const newForm = await createForm(templateId, clientId);
                navigateTo('fill-form', { form: newForm });
              }}
              onCancel={() => {
                if (selectedClient) {
                  navigateTo('client-forms', { client: selectedClient });
                  return;
                }

                navigateTo('dashboard');
              }}
              controls={navigationControls.newForm}
              onControlsChange={(updates) => updateControls('newForm', updates)}
              helpConfig={visibleHelpConfig.newForm}
            />
          </div>
        )}

        {currentView === 'fill-form' && selectedForm && (
          <FormFiller
            form={selectedForm}
            template={templates.find((template) => template.id === selectedForm.templateId)}
            client={clients.find((client) => client.id === selectedForm.clientId)}
            onUpdate={updateForm}
            onClose={() => navigateTo('dashboard')}
            onDelete={() => deleteForm(selectedForm.id)}
            titleFocusRequest={formTitleFocusRequest}
            titleFocusFormId={formTitleFocusFormId}
            readOnlyMessage={selectedFormReadOnlyMessage}
          />
        )}

        {currentView === 'fill-form' && !selectedForm && (
          <div className="card">
            <h2 className="card-title">Form not found</h2>
            <p>The selected form is no longer available.</p>
            <button className="btn btn-secondary" onClick={() => navigateTo('dashboard')}>
              Back to Dashboard
            </button>
          </div>
        )}
      </main>

      {shareDialogForm && (
        <div className="df-modal-backdrop" role="presentation" onClick={closeShareDialog}>
          <div
            className="df-share-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="df-share-dialog-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="df-share-dialog-header">
              <div>
                <h3 id="df-share-dialog-title">Public share link</h3>
                <p>{shareDialogForm.title || 'Untitled Form'}</p>
              </div>
              <button className="btn btn-outline btn-sm" type="button" onClick={closeShareDialog}>Close</button>
            </div>

            <p className="df-share-warning">
              Public links let anyone with the URL view and submit this form. Share only with the intended recipient.
            </p>

            <div className="df-share-options">
              <label className="checkbox-item">
                <input
                  type="checkbox"
                  checked={shareOptions.allowMultiplePublicSubmissions}
                  disabled={shareLinkState.saving || Boolean(shareLinkState.link?.active)}
                  onChange={(event) => setShareOptions({ allowMultiplePublicSubmissions: event.target.checked })}
                />
                <span>Allow multiple public submissions from this form link</span>
              </label>
              <p>
                {shareOptions.allowMultiplePublicSubmissions
                  ? 'Each public visit creates a separate response and can be submitted independently.'
                  : 'The public link follows the standard single-use behavior.'}
              </p>
              {shareLinkState.link?.active && (
                <p className="text-muted">Revoke the active link before changing this per-form setting.</p>
              )}
            </div>

            {shareLinkState.loading ? (
              <p>Loading share-link status...</p>
            ) : (
              <>
                {shareLinkState.error && <div className="alert alert-error">{shareLinkState.error}</div>}

                {shareLinkState.link?.active && shareLinkState.link?.token ? (
                  <div className="df-share-link-box">
                    <label htmlFor="df-share-link-url">Share URL</label>
                    <input
                      id="df-share-link-url"
                      className="form-control"
                      readOnly
                      value={buildPublicShareUrl(shareLinkState.link.token)}
                      onFocus={(event) => event.target.select()}
                    />
                    {shareLinkState.link?.remembered && (
                      <p className="text-muted">Showing the share URL remembered by this browser.</p>
                    )}
                    <div className="df-share-dialog-actions">
                      <button className="btn btn-primary btn-sm" type="button" onClick={copyShareLink} disabled={shareLinkState.saving}>
                        {shareLinkState.copied ? 'Copied' : 'Copy link'}
                      </button>
                      {shareDialogForm.allowMultiplePublicSubmissions && (
                        <button className="btn btn-secondary btn-sm" type="button" onClick={() => downloadResponsesCsv(shareDialogForm)} disabled={shareLinkState.saving}>
                          Download responses CSV
                        </button>
                      )}
                      <button className="btn btn-danger btn-sm" type="button" onClick={revokeShareLink} disabled={shareLinkState.saving}>
                        Revoke link
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="df-share-empty">
                    <p>No active public link exists for this form.</p>
                    <button className="btn btn-primary" type="button" onClick={createShareLink} disabled={shareLinkState.saving}>
                      {shareLinkState.saving ? 'Creating...' : 'Create share link'}
                    </button>
                  </div>
                )}

                {shareLinkState.link?.active && !shareLinkState.link?.token && (
                  <div className="df-share-empty">
                    <p>An active link exists, but it was created before share URLs were saved for display. Create a new link to show and copy it here.</p>
                    <button className="btn btn-primary" type="button" onClick={createShareLink} disabled={shareLinkState.saving}>
                      {shareLinkState.saving ? 'Rotating...' : 'Create new link'}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default App;






